import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  purchaseProposalCompanyGetAll,
  purchaseProposalCompanyExcel,
} from '../../../redux/slices/purchase.slice'
import {categoryGetAll} from '../../../redux/slices/category.slice'

import {
  useMemoizedStaticData
} from '../../../utils/performanceOptimizations'

const selectPurchaseData = (state) => ({
  proposalCompany: state.purchase.proposalCompany,
  loading: state.purchase.loading
})

const selectCategoryData = (state) => ({
  category: state.category.category
})

function List() {
  const intl = useIntl()
  const dispatch = useDispatch()

  const {proposalCompany, loading} = useSelector(selectPurchaseData)
  const {category} = useSelector(selectCategoryData)

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) {
      dispatch(purchaseProposalCompanyGetAll(params))
      dispatch(categoryGetAll({SourceTypeId: 34}))
    }
  }, [params, dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const handleExcelExport = useCallback(() => {
    if (params) {
      dispatch(
        purchaseProposalCompanyExcel({
          params,
          fileName: intl.formatMessage({id: 'BC.PROPOSALCOMPANY'}),
        })
      )
    }
  }, [dispatch, params, intl])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.SEARCH.NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.SEARCH.EMAIL'})}
          className='min-w-125px'
        />
      ),
      accessor: 'email',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.SEARCH.PHONE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'phone',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.SEARCH.CATEGORY'})}
          className='min-w-125px'
        />
      ),
      accessor: 'categoryName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
  ], [intl, handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'PURCHASEPROPOSALCOMPANY.SEARCH.NAME',
          name: 'name',
          type: 'text',
        },
        {
          title: 'PURCHASEPROPOSALCOMPANY.SEARCH.EMAIL',
          name: 'email',
          type: 'text',
        },
        {
          title: 'PURCHASEPROPOSALCOMPANY.SEARCH.PHONE',
          name: 'phone',
          type: 'text',
        },
        {
          title: 'PURCHASEPROPOSALCOMPANY.SEARCH.CATEGORY',
          data: category,
          name: 'categoryId',
        },
      ],
    },
  ], [category, handleExcelExport])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.PROPOSALCOMPANY'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={'PROPOSALCOMPANY'}
        />
        <KTCardBody>
          {!proposalCompany && <ListLoading />}
          {proposalCompany && (
            <Table
              items={proposalCompany}
              cols={columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
